import {HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest} from "@angular/common/http";
import {inject} from "@angular/core";
import {Router} from "@angular/router";
import {catchError, switchMap} from "rxjs/operators";
import {EMPTY, from, Observable, throwError} from "rxjs";
import {AuthService} from "../services/auth.service";
import {ToastService} from "../services/toast.service";
import {ElevationGuardService, ElevationReason} from "../services/elevation-guard.service";
import {EXPORT_SORT_REWRITTEN} from "./export-paging.interceptor";
import {HANDLED_ERROR_CODES} from "../http/handled-error-codes";
import {QUIET_ERRORS} from "../http/silent-request";

/**
 * Shape of the 401 body the backend emits from
 * `gozem-api/src/plugins/error.js`. The `elevation_reason` +
 * `elevation_token` fields are only present when the RBAC policy
 * engine flagged the request as needing JIT elevation / MFA.
 */
interface ApiErrorBody {
    code?: string;
    error_code?: string;
    message?: string;
    elevation_reason?: ElevationReason;
    elevation_token?: string;
}

/**
 * Centralised HTTP error handling. 401 is the interesting case:
 *
 *   1. **Body carries `elevation_reason`** — RBAC policy engine says
 *      the admin needs JIT elevation (or MFA). Route into the
 *      ElevationGuardService which opens the request modal. NO
 *      logout, NO toast — the modal is the recovery UX.
 *
 *   2. **Body has `code: "011"` and NO elevation_reason** — plain
 *      "not authorized" from RBAC. Surface a toast (or let the
 *      component-level `catchError` handle it) but DO NOT clear
 *      the session. The user is logged in fine; they just can't
 *      do THIS action. Kicking them to /login would be a bug.
 *
 *   3. **Any other 401, or a 400 token refusal** — session expiry /
 *      invalid signature / missing header. The API answers an expired
 *      bearer with HTTP 400 "Token has expired" (`ResultCode.TOKEN_EXPIRED`
 *      is not defined in @gozem/core, so the error falls through to the
 *      generic handler) and a malformed one with 400 `code: "451"`;
 *      without matching those here the admin got that message as a
 *      toast and the page failed, instead of the refresh / login below. Try to refresh the access token first
 *      (silent recovery — the admin doesn't notice their token
 *      rolled over). If refresh works, retry the original request
 *      transparently. If it fails, clear the session, bounce to
 *      /login, and SWALLOW the error via EMPTY so downstream
 *      `.subscribe({error})` handlers do NOT ALSO fire their per-
 *      page "Erreur X" or "Impossible de récupérer les données"
 *      toast on top of the auto-logout — the /login redirect is
 *      the whole message.
 *
 * See `error.js` (backend) for the forwarded fields, `AGENTS.md`
 * §2.5 for the JIT elevation contract, and `RBAC-V2-BACKLOG.md`
 * Section B for the roadmap.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
    const auth = inject(AuthService);
    const router = inject(Router);
    const toast = inject(ToastService);
    const elevation = inject(ElevationGuardService);

    const deps: ErrorDeps = {auth, router, toast, elevation};

    return next(req).pipe(
        catchError((err: HttpErrorResponse) => {
            // An export page whose `sort` the export-paging interceptor rewrote
            // and the endpoint refused: that interceptor re-sends the original
            // request — not the admin's error, no toast.
            if (err.status === 400 && req.context.get(EXPORT_SORT_REWRITTEN)) {
                return throwError(() => err);
            }
            const body: ApiErrorBody =
                typeof err.error === "object" && err.error !== null ? (err.error as ApiErrorBody) : {};

            if (err.status === 401 || (err.status === 400 && isTokenRefusal(body))) {
                return handleAuthError(req, next, err, body, deps);
            }
            reportError(req, err, body, toast);
            return throwError(() => err);
        })
    );
};

/** The services the error interceptor acts through. */
interface ErrorDeps {
    auth: AuthService;
    router: Router;
    toast: ToastService;
    elevation: ElevationGuardService;
}

/** A 401, or a 400 token refusal: branches 1–3 of `errorInterceptor`. */
function handleAuthError(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    err: HttpErrorResponse,
    body: ApiErrorBody,
    deps: ErrorDeps
): Observable<HttpEvent<unknown>> {
    const {auth, router, toast, elevation} = deps;
    // Branch 1 — elevation flow.
    if (body.elevation_reason && body.elevation_token) {
        elevation.open(body.elevation_token, body.elevation_reason);
        return throwError(() => err);
    }
    // The backend uses `code: "011"` (NOT_AUTHORIZED) for
    // BOTH genuine RBAC denials AND for expired/invalid
    // tokens (see @gozem/core ResultCode.NOT_AUTHORIZED,
    // status 401). Distinguishing them purely from the body
    // is impossible, so we inspect the LOCAL JWT before
    // deciding:
    //   • Access token missing OR its `exp` is in the past
    //     → session-side death. Fall through to branch 3
    //     (try refresh, then force logout on failure) so
    //     the admin isn't stuck on a page that will 401
    //     every action forever.
    //   • Access token present and not-yet-expired
    //     → genuine RBAC denial. Toast, stay on page.
    //
    // Prior versions used branch 2 unconditionally on
    // `code === "011"` — which is why expired tokens
    // surfaced an endless "Accès refusé" toast instead of
    // bouncing to /login.
    if (body.code === "011" && !auth.isAccessExpired()) {
        // Branch 2 — genuine RBAC denial. Session still
        // valid, admin just can't do THIS action.
        if (req.context.get(QUIET_ERRORS)) {
            return throwError(() => err);
        }
        toast.warning("Accès refusé", body.message || "Vous n'avez pas la permission pour cette action.");
        return throwError(() => err);
    }
    // Branch 3 — session-side 401. Do NOT logout blindly:
    // try the silent refresh first. If it succeeds, replay
    // the original request with the new Bearer token — the
    // admin never sees the interruption.
    //
    // Skip the refresh for /auth/refresh itself (would loop)
    // and for /auth/login (the login flow has its own error
    // surfacing — we must not swallow those errors here).
    if (req.url.includes("/auth/refresh")) {
        // Refresh itself failed — session is gone. Kick
        // to /login and swallow so the caller doesn't
        // fire a redundant toast.
        forceLogout(auth, router, toast);
        return EMPTY;
    }
    if (req.url.includes("/auth/login")) {
        // Login call — surface the error to the login page.
        return throwError(() => err);
    }
    return from(auth.refreshAccessToken()).pipe(
        switchMap((newToken) => {
            if (!newToken) {
                // Refresh failed → real logout.
                forceLogout(auth, router, toast);
                return EMPTY;
            }
            // Retry with the new access token attached. Every
            // outbound request is re-run through the whole
            // interceptor chain, so `authInterceptor` will
            // re-read the (now updated) token from AuthService.
            const retried = req.clone({
                setHeaders: {Authorization: `Bearer ${newToken}`}
            });
            return next(retried);
        })
    );
}

/** Any other error: toast it unless the request asked for quiet or its caller handles it. */
function reportError(req: HttpRequest<unknown>, err: HttpErrorResponse, body: ApiErrorBody, toast: ToastService): void {
    if (req.context.get(QUIET_ERRORS)) {
        // A background read (a list's live refresh — silent-request.ts runSilently) fails quietly.
        return;
    }
    if (err.status === 0) {
        toast.error("Impossible de joindre le serveur — vérifiez votre connexion.");
        return;
    }
    if (isHandledRefusal(body, req.context.get(HANDLED_ERROR_CODES))) {
        // The caller translates this business refusal itself (handled-error-codes.ts).
        return;
    }
    const msg = body.message || err.message || `Erreur ${err.status}`;
    toast.error(msg);
}

/**
 * Clears the session and bounces the admin to /login with a one-shot
 * toast explaining why. Called from both the "session expired, no
 * refresh available" branch and the "refresh call itself failed" branch
 * so the messaging is consistent regardless of how the session died.
 *
 * The redirect includes `?next=<url>` so the login page can bring the
 * admin back once they re-authenticate. When the 401 fires during a
 * navigation (a guard or resolver call), `next` is the page the admin
 * clicked, not the one they were leaving.
 */
function forceLogout(auth: AuthService, router: Router, toast: ToastService): void {
    auth.clearToken();
    toast.warning("Session expirée", "Vous avez été déconnecté(e) automatiquement. Reconnectez-vous pour continuer.");
    const nav = router.currentNavigation();
    const next = nav ? router.serializeUrl(nav.finalUrl ?? nav.extractedUrl) : router.url;
    // Guard against loops when the 401 fires WHILE we're already on
    // /login (e.g. background call from a leftover component). Angular's
    // Router deduplicates identical urls but the guard is cheap.
    if (router.url.split("?")[0] !== "/login" && next.split("?")[0] !== "/login") {
        void router.navigate(["/login"], {queryParams: {next}});
    }
}

/**
 * A 400 the API sends for a dead bearer: an expired token (generic
 * error carrying "Token has expired") or `ResultCode.INVALID_TOKEN`
 * (`code: "451"`). See branch 3 of `errorInterceptor`.
 */
function isTokenRefusal(body: ApiErrorBody): boolean {
    return body.code === "451" || /token has expired/i.test(body.message ?? "");
}

/**
 * A refusal the caller translates itself: its `error_code`, or (refusals without one, e.g. the
 * dispatch center's `intention_mismatch`) its `code`, is in the request's handled list.
 */
function isHandledRefusal(body: {error_code?: unknown; code?: unknown}, handled: readonly string[]): boolean {
    const listed = (v: unknown) => typeof v === "string" && v !== "" && handled.includes(v);
    return listed(body.error_code) || listed(body.code);
}
